/**
 * Illustrative diagrams for each tab detail page (§6) and for the
 * /what-is-dideck landing page. One SVG per surface, all sharing a
 * consistent sovereign-purple visual system so the site reads as one
 * design family.
 *
 * Each diagram is a small, self-contained SVG (viewBox 800×360) meant
 * to convey the tab\'s mental model at a glance — not to replace the
 * paragraphs. Rendered below the first paragraph so first-time
 * readers get a visual anchor before the deeper prose.
 */

const C = {
  primary: '#3D2670',
  primaryLight: '#5E40A0',
  primaryDark: '#2A1A53',
  ink: '#241a38',
  inkMute: '#574c6e',
  inkDim: '#7b7194',
  white: '#ffffff',
  bg: '#ffffff',
  bgAlt: '#f2eefb',
  bgHi: '#e3d9f4',
  border: '#cdbfe6',
  green: '#1f8f3d',
  amber: '#a8710f',
  red: '#cf2626',
} as const;

const DIAGRAM_SLUGS = new Set([
  'what-is-dideck',
  'cockpit',
  'sovereign-proxy',
  'hierarchy',
  'targets',
  'decisions',
  'synapse',
  'ai-gateway',
  'genwork',
  'what-if',
  'insights',
  'developers',
  'ledger',
  'ml-layer',
  'eu-ai-act',
]);

export function hasDiagram(slug: string): boolean {
  return DIAGRAM_SLUGS.has(slug);
}

export function TabDiagram({ slug }: { slug: string }): JSX.Element | null {
  const Body = DIAGRAM_BY_SLUG[slug];
  if (!Body) return null;
  return (
    <figure
      aria-hidden
      className="my-8 overflow-hidden rounded-md border border-sovereign-line bg-white shadow-card"
    >
      <Body />
    </figure>
  );
}

// ── Shared SVG utilities ─────────────────────────────────────────────

function Frame({ children }: { children: React.ReactNode }): JSX.Element {
  return (
    <svg
      viewBox="0 0 800 360"
      xmlns="http://www.w3.org/2000/svg"
      preserveAspectRatio="xMidYMid meet"
      className="block h-auto w-full"
    >
      <defs>
        <marker
          id="arrow"
          viewBox="0 0 10 10"
          refX="8"
          refY="5"
          markerWidth="6"
          markerHeight="6"
          orient="auto-start-reverse"
        >
          <path d="M 0 0 L 10 5 L 0 10 z" fill={C.primary} />
        </marker>
        <marker
          id="arrow-mute"
          viewBox="0 0 10 10"
          refX="8"
          refY="5"
          markerWidth="6"
          markerHeight="6"
          orient="auto-start-reverse"
        >
          <path d="M 0 0 L 10 5 L 0 10 z" fill={C.inkMute} />
        </marker>
      </defs>
      {children}
    </svg>
  );
}

function Box({
  x,
  y,
  w,
  h,
  fill = C.white,
  stroke = C.border,
  strokeWidth = 1.5,
  radius = 8,
}: {
  x: number;
  y: number;
  w: number;
  h: number;
  fill?: string;
  stroke?: string;
  strokeWidth?: number;
  radius?: number;
}): JSX.Element {
  return (
    <rect x={x} y={y} width={w} height={h} rx={radius} fill={fill} stroke={stroke} strokeWidth={strokeWidth} />
  );
}

function Label({
  x,
  y,
  children,
  size = 13,
  weight = 600,
  color = C.ink,
  anchor = 'middle',
}: {
  x: number;
  y: number;
  children: string;
  size?: number;
  weight?: number | string;
  color?: string;
  anchor?: 'start' | 'middle' | 'end';
}): JSX.Element {
  return (
    <text
      x={x}
      y={y}
      fontFamily="Inter, system-ui, sans-serif"
      fontSize={size}
      fontWeight={weight}
      fill={color}
      textAnchor={anchor}
      dominantBaseline="middle"
    >
      {children}
    </text>
  );
}

function Arrow({
  x1,
  y1,
  x2,
  y2,
  color = C.primary,
  dashed = false,
}: {
  x1: number;
  y1: number;
  x2: number;
  y2: number;
  color?: string;
  dashed?: boolean;
}): JSX.Element {
  return (
    <line
      x1={x1}
      y1={y1}
      x2={x2}
      y2={y2}
      stroke={color}
      strokeWidth={1.8}
      strokeDasharray={dashed ? '4 3' : undefined}
      markerEnd={`url(#${color === C.primary ? 'arrow' : 'arrow-mute'})`}
    />
  );
}

// ── The 15 diagrams ──────────────────────────────────────────────────

function DiagramWhatIsDiDeck(): JSX.Element {
  return (
    <Frame>
      {/* Tenant boundary */}
      <rect x={30} y={30} width={740} height={300} rx={12} fill={C.bgAlt} stroke={C.primary} strokeWidth={2} strokeDasharray="6 4" />
      <Label x={60} y={52} anchor="start" size={11} weight={700} color={C.primary}>YOUR TENANT BOUNDARY</Label>

      {/* Cockpit */}
      <Box x={70} y={90} w={180} h={90} fill={C.white} stroke={C.primary} />
      <Label x={160} y={118} size={14} weight={700}>The Cockpit</Label>
      <Label x={160} y={140} size={11} weight={400} color={C.inkMute}>Managers, Missions,</Label>
      <Label x={160} y={155} size={11} weight={400} color={C.inkMute}>Decisions, Drafts</Label>

      {/* Sovereign Proxy */}
      <Box x={310} y={90} w={180} h={90} fill={C.primary} stroke={C.primaryDark} />
      <Label x={400} y={118} size={14} weight={700} color={C.white}>Sovereign Proxy</Label>
      <Label x={400} y={140} size={11} weight={400} color={C.white}>Classify · Redact ·</Label>
      <Label x={400} y={155} size={11} weight={400} color={C.white}>Route · Anchor</Label>

      {/* Model Router */}
      <Box x={550} y={90} w={180} h={90} fill={C.white} stroke={C.primary} />
      <Label x={640} y={118} size={14} weight={700}>Model Router</Label>
      <Label x={640} y={140} size={11} weight={400} color={C.inkMute}>Frontier · VPC ·</Label>
      <Label x={640} y={155} size={11} weight={400} color={C.inkMute}>On-prem · Sovereign LLM</Label>

      <Arrow x1={250} y1={135} x2={310} y2={135} />
      <Arrow x1={490} y1={135} x2={550} y2={135} />

      {/* Ledger — bottom bar */}
      <Box x={70} y={220} w={660} h={60} fill={C.bgHi} stroke={C.primary} />
      <Label x={400} y={240} size={13} weight={700} color={C.primary}>Immutable Ledger — hash-chained, signed, exportable</Label>
      <Label x={400} y={260} size={11} weight={400} color={C.inkMute}>Every AI call · every decision · every promotion anchored here</Label>

      <Arrow x1={160} y1={180} x2={160} y2={220} color={C.primary} />
      <Arrow x1={400} y1={180} x2={400} y2={220} color={C.primary} />
      <Arrow x1={640} y1={180} x2={640} y2={220} color={C.primary} />

      <Label x={60} y={310} anchor="start" size={11} weight={600} color={C.inkMute}>
        Nothing leaves this boundary. Sovereign Machine Learning Layer (S1–S5) attaches at every seam.
      </Label>
    </Frame>
  );
}

function DiagramCockpit(): JSX.Element {
  return (
    <Frame>
      {/* Outer window */}
      <Box x={40} y={30} w={720} h={300} fill={C.bgAlt} stroke={C.primary} strokeWidth={2} />
      {/* Top toolbar */}
      <Box x={40} y={30} w={720} h={34} fill={C.primary} stroke={C.primary} radius={8} />
      <Label x={60} y={47} anchor="start" size={11} weight={700} color={C.white}>Top Integration Toolbar · source health every 10 s</Label>
      <circle cx={640} cy={47} r={5} fill={C.green} />
      <circle cx={660} cy={47} r={5} fill={C.green} />
      <circle cx={680} cy={47} r={5} fill={C.amber} />
      <circle cx={700} cy={47} r={5} fill={C.green} />
      <circle cx={720} cy={47} r={5} fill={C.green} />

      {/* Left command toolbar */}
      <Box x={40} y={64} w={70} h={266} fill={C.primaryDark} stroke={C.primary} radius={0} />
      {['◧', '★', '⚑', '⚖', '✎', '◎', '⟳', '⌘', '☰'].map((g, i) => (
        <g key={i}>
          <rect x={52} y={76 + i * 28} width={46} height={22} rx={4} fill={C.white} opacity={0.15} />
          <Label x={75} y={87 + i * 28} size={12} weight={600} color={C.white}>{g}</Label>
        </g>
      ))}

      {/* Workspace windows */}
      <Box x={130} y={80} w={200} h={110} fill={C.white} />
      <Box x={130} y={80} w={200} h={22} fill={C.primaryLight} radius={6} />
      <Label x={140} y={92} anchor="start" size={11} weight={700} color={C.white}>Insights</Label>
      <Label x={230} y={130} size={11} color={C.inkMute}>Top-3 missions · today</Label>

      <Box x={350} y={80} w={230} h={140} fill={C.white} />
      <Box x={350} y={80} w={230} h={22} fill={C.primary} radius={6} />
      <Label x={360} y={92} anchor="start" size={11} weight={700} color={C.white}>Decision Queue</Label>
      <Label x={465} y={130} size={11} color={C.inkMute}>3 signatures pending</Label>
      <Label x={465} y={150} size={11} color={C.inkMute}>+ step-up MFA</Label>

      <Box x={130} y={205} w={200} h={110} fill={C.white} />
      <Box x={130} y={205} w={200} h={22} fill={C.green} radius={6} />
      <Label x={140} y={217} anchor="start" size={11} weight={700} color={C.white}>Missions</Label>
      <Label x={230} y={255} size={11} color={C.inkMute}>7 active · 2 amber</Label>

      {/* Right context pane */}
      <Box x={600} y={80} w={150} h={240} fill={C.white} stroke={C.border} />
      <Label x={675} y={97} size={11} weight={700} color={C.primary}>Right Context</Label>
      <Label x={675} y={125} size={10} color={C.inkMute}>Ledger tail · RAG</Label>
      <Label x={675} y={140} size={10} color={C.inkMute}>hits · source rows</Label>
    </Frame>
  );
}

function DiagramSovereignProxy(): JSX.Element {
  return (
    <Frame>
      <Label x={400} y={30} size={11} weight={700} color={C.inkMute}>Every AI call flows through the Sovereign Proxy</Label>

      {/* Input clients */}
      {['Cockpit', 'Mobile', 'Do-It', 'IDE'].map((s, i) => (
        <g key={s}>
          <Box x={40} y={80 + i * 50} w={110} h={36} fill={C.bgAlt} />
          <Label x={95} y={98 + i * 50} size={12} weight={600}>{s}</Label>
          <Arrow x1={150} y1={98 + i * 50} x2={250} y2={190} />
        </g>
      ))}

      {/* Sovereign Proxy */}
      <Box x={250} y={100} w={300} h={180} fill={C.primary} stroke={C.primaryDark} radius={10} />
      <Label x={400} y={125} size={14} weight={700} color={C.white}>Sovereign Proxy</Label>
      <Box x={275} y={140} w={250} h={28} fill={C.white} radius={5} />
      <Label x={400} y={154} size={11} weight={600}>Classify · 4 sensitivity tiers</Label>
      <Box x={275} y={175} w={250} h={28} fill={C.white} radius={5} />
      <Label x={400} y={189} size={11} weight={600}>Redact · HMAC-derived placeholders</Label>
      <Box x={275} y={210} w={250} h={28} fill={C.white} radius={5} />
      <Label x={400} y={224} size={11} weight={600}>Route · mission × sensitivity</Label>
      <Box x={275} y={245} w={250} h={28} fill={C.white} radius={5} />
      <Label x={400} y={259} size={11} weight={600}>Anchor · ledger entry per call</Label>

      {/* Endpoints */}
      {[
        { label: 'External frontier', color: C.bgAlt, y: 90 },
        { label: 'Tenant VPC', color: C.bgHi, y: 155 },
        { label: 'On-prem SLM', color: C.bgAlt, y: 220 },
        { label: 'Sovereign LLM (S4)', color: C.primaryLight, y: 285 },
      ].map((e, i) => (
        <g key={i}>
          <Box x={620} y={e.y} w={150} h={40} fill={e.color} />
          <Label x={695} y={e.y + 20} size={12} weight={600} color={e.color === C.primaryLight ? C.white : C.ink}>{e.label}</Label>
          <Arrow x1={550} y1={190} x2={620} y2={e.y + 20} />
        </g>
      ))}

      <Label x={400} y={340} size={11} weight={600} color={C.red}>Fails closed — no silent fallback to an unsafe path</Label>
    </Frame>
  );
}

function DiagramHierarchy(): JSX.Element {
  const nodes = [
    { id: 'ceo', x: 400, y: 60, label: 'CEO', sub: 'Position 001' },
    { id: 'vp1', x: 200, y: 170, label: 'VP-Ops', sub: 'reports-to CEO' },
    { id: 'vp2', x: 600, y: 170, label: 'VP-Finance', sub: 'reports-to CEO' },
    { id: 'm1', x: 100, y: 280, label: 'Mgr A', sub: '' },
    { id: 'm2', x: 260, y: 280, label: 'Mgr B', sub: '' },
    { id: 'm3', x: 520, y: 280, label: 'Mgr C', sub: '' },
    { id: 'm4', x: 680, y: 280, label: 'Mgr D', sub: '' },
  ];
  const edges: [string, string][] = [
    ['ceo', 'vp1'], ['ceo', 'vp2'],
    ['vp1', 'm1'], ['vp1', 'm2'],
    ['vp2', 'm3'], ['vp2', 'm4'],
  ];
  const byId = Object.fromEntries(nodes.map((n) => [n.id, n]));
  return (
    <Frame>
      {edges.map(([a, b], i) => (
        <line
          key={i}
          x1={byId[a]!.x}
          y1={byId[a]!.y + 25}
          x2={byId[b]!.x}
          y2={byId[b]!.y - 25}
          stroke={C.border}
          strokeWidth={1.5}
        />
      ))}
      {nodes.map((n) => (
        <g key={n.id}>
          <Box x={n.x - 60} y={n.y - 25} w={120} h={50} fill={C.white} stroke={C.primary} />
          <Label x={n.x} y={n.y - 6} size={12} weight={700}>{n.label}</Label>
          {n.sub && <Label x={n.x} y={n.y + 12} size={10} color={C.inkMute}>{n.sub}</Label>}
        </g>
      ))}
      {/* Signature rule badge on VP-Finance */}
      <Box x={520} y={110} w={160} h={36} fill={C.primaryLight} radius={18} />
      <Label x={600} y={128} size={11} weight={700} color={C.white}>Signature Rule: 2 signers</Label>
      <line x1={600} y1={146} x2={600} y2={170} stroke={C.primaryLight} strokeDasharray="3 3" />
      <Label x={400} y={340} size={11} weight={600} color={C.inkMute}>
        Cascade routes decisions down the reports-to graph automatically
      </Label>
    </Frame>
  );
}

function DiagramTargets(): JSX.Element {
  return (
    <Frame>
      <Label x={400} y={30} size={11} weight={700} color={C.inkMute}>KPI over time — bands + pre-threshold drift signal</Label>
      {/* Axes */}
      <line x1={70} y1={280} x2={730} y2={280} stroke={C.inkDim} />
      <line x1={70} y1={80} x2={70} y2={280} stroke={C.inkDim} />
      {/* Bands */}
      <rect x={70} y={80} width={660} height={60} fill={C.red} opacity={0.12} />
      <rect x={70} y={140} width={660} height={60} fill={C.amber} opacity={0.14} />
      <rect x={70} y={200} width={660} height={80} fill={C.green} opacity={0.14} />
      <Label x={720} y={110} anchor="end" size={10} weight={700} color={C.red}>RED</Label>
      <Label x={720} y={170} anchor="end" size={10} weight={700} color={C.amber}>AMBER</Label>
      <Label x={720} y={240} anchor="end" size={10} weight={700} color={C.green}>GREEN</Label>
      {/* Line series */}
      <polyline
        fill="none"
        stroke={C.primary}
        strokeWidth={2.4}
        points="90,260 150,255 210,245 270,235 330,220 390,205 450,190 500,175"
      />
      {/* Actual observed to date */}
      {[{ x: 90, y: 260 }, { x: 150, y: 255 }, { x: 210, y: 245 }, { x: 270, y: 235 }, { x: 330, y: 220 }, { x: 390, y: 205 }, { x: 450, y: 190 }, { x: 500, y: 175 }].map((p, i) => (
        <circle key={i} cx={p.x} cy={p.y} r={3.5} fill={C.primary} />
      ))}
      {/* Drift signal projection */}
      <polyline
        fill="none"
        stroke={C.primaryLight}
        strokeWidth={2}
        strokeDasharray="5 4"
        points="500,175 570,150 640,130 710,115"
      />
      {/* Signal badge */}
      <Box x={430} y={95} w={220} h={34} fill={C.primary} radius={17} />
      <Label x={540} y={112} size={11} weight={700} color={C.white}>Pre-threshold drift signal fired</Label>
      <line x1={540} y1={129} x2={610} y2={148} stroke={C.primary} strokeDasharray="3 3" />
      <circle cx={610} cy={148} r={5} fill={C.primaryLight} stroke={C.primary} strokeWidth={2} />
      {/* Axis labels */}
      <Label x={400} y={300} size={11} color={C.inkMute}>time</Label>
      <Label x={40} y={180} size={11} color={C.inkMute}>KPI</Label>
    </Frame>
  );
}

function DiagramDecisions(): JSX.Element {
  return (
    <Frame>
      {/* Decision card */}
      <Box x={40} y={130} w={190} h={100} fill={C.white} stroke={C.primary} />
      <Box x={40} y={130} w={190} h={26} fill={C.primary} radius={6} />
      <Label x={135} y={144} size={12} weight={700} color={C.white}>Decision object</Label>
      <Label x={135} y={175} size={10} color={C.inkMute}>Redacted prompt + hits</Label>
      <Label x={135} y={192} size={10} color={C.inkMute}>Model output</Label>
      <Label x={135} y={209} size={10} color={C.inkMute}>Do-It action + magnitude</Label>

      <Arrow x1={230} y1={180} x2={290} y2={180} />

      {/* Signatures */}
      <Box x={290} y={100} w={170} h={60} fill={C.bgAlt} stroke={C.primary} />
      <Label x={375} y={122} size={11} weight={700}>Signature 1</Label>
      <Label x={375} y={140} size={10} color={C.inkMute}>Category Manager · step-up MFA</Label>

      <Box x={290} y={200} w={170} h={60} fill={C.bgAlt} stroke={C.primary} />
      <Label x={375} y={222} size={11} weight={700}>Signature 2</Label>
      <Label x={375} y={240} size={10} color={C.inkMute}>Controller · step-up MFA</Label>

      <line x1={230} y1={180} x2={290} y2={130} stroke={C.primary} strokeWidth={1.5} />
      <line x1={230} y1={180} x2={290} y2={230} stroke={C.primary} strokeWidth={1.5} />

      <Arrow x1={460} y1={130} x2={540} y2={170} />
      <Arrow x1={460} y1={230} x2={540} y2={190} />

      {/* Do-It */}
      <Box x={540} y={150} w={110} h={60} fill={C.primary} stroke={C.primaryDark} />
      <Label x={595} y={175} size={12} weight={700} color={C.white}>Do-It</Label>
      <Label x={595} y={192} size={10} color={C.white}>Source-system mutation</Label>

      <Arrow x1={650} y1={180} x2={720} y2={180} />

      {/* Ledger */}
      <Box x={720} y={150} w={60} h={60} fill={C.bgHi} stroke={C.primary} />
      <Label x={750} y={175} size={11} weight={700} color={C.primary}>Ledger</Label>
      <Label x={750} y={192} size={10} color={C.primary}>entry</Label>

      <Label x={400} y={330} size={11} weight={600} color={C.inkMute}>
        Signature Rule enforced at Sovereign Proxy · idempotency prevents double-commit
      </Label>
    </Frame>
  );
}

function DiagramSynapse(): JSX.Element {
  const steps = [
    { label: 'Record', sub: 'per-participant consent' },
    { label: 'Seal', sub: 'WORM Vault, per tenant' },
    { label: 'Summarise', sub: 'via Sovereign Proxy' },
    { label: 'Confirm + Distribute', sub: 'step-up MFA required' },
  ];
  const boxW = 150;
  const gap = 30;
  const startX = (800 - (steps.length * boxW + (steps.length - 1) * gap)) / 2;
  return (
    <Frame>
      <Label x={400} y={40} size={12} weight={700} color={C.inkMute}>
        Meetings + calls, captured under consent, sealed, summarised, distributed
      </Label>
      {steps.map((s, i) => {
        const x = startX + i * (boxW + gap);
        return (
          <g key={i}>
            <Box x={x} y={120} w={boxW} h={100} fill={i === steps.length - 1 ? C.primary : C.white} stroke={C.primary} />
            <Label x={x + boxW / 2} y={155} size={13} weight={700} color={i === steps.length - 1 ? C.white : C.ink}>
              {`${i + 1}. ${s.label}`}
            </Label>
            <Label x={x + boxW / 2} y={185} size={10} color={i === steps.length - 1 ? C.white : C.inkMute}>
              {s.sub}
            </Label>
            {i < steps.length - 1 && (
              <Arrow x1={x + boxW + 4} y1={170} x2={x + boxW + gap - 4} y2={170} />
            )}
          </g>
        );
      })}
      {/* Ledger bar */}
      <Box x={80} y={260} w={640} h={44} fill={C.bgHi} stroke={C.primary} />
      <Label x={400} y={283} size={11} weight={700} color={C.primary}>
        Ledger entry at every step · decisions, action items, RAG-band changes extracted
      </Label>
    </Frame>
  );
}

function DiagramAiGateway(): JSX.Element {
  const providers = [
    { label: 'Anthropic', color: C.bgAlt, angle: -110 },
    { label: 'OpenAI', color: C.bgAlt, angle: -70 },
    { label: 'Gemini', color: C.bgAlt, angle: -30 },
    { label: 'Azure', color: C.bgAlt, angle: 30 },
    { label: 'Bedrock', color: C.bgAlt, angle: 70 },
    { label: 'Sovereign LLM (S4)', color: C.primaryLight, angle: 110 },
  ];
  const cx = 400;
  const cy = 190;
  const r = 130;
  return (
    <Frame>
      <Label x={400} y={30} size={11} weight={700} color={C.inkMute}>Model Router · one place to connect + route</Label>
      {providers.map((p, i) => {
        const rad = (p.angle * Math.PI) / 180;
        const px = cx + r * Math.cos(rad);
        const py = cy + r * Math.sin(rad);
        return (
          <g key={i}>
            <line x1={cx} y1={cy} x2={px} y2={py} stroke={C.border} strokeWidth={1.5} />
            <Box x={px - 60} y={py - 18} w={120} h={36} fill={p.color} stroke={C.primary} />
            <Label x={px} y={py} size={11} weight={600} color={p.color === C.primaryLight ? C.white : C.ink}>{p.label}</Label>
          </g>
        );
      })}
      {/* Router hub */}
      <circle cx={cx} cy={cy} r={44} fill={C.primary} stroke={C.primaryDark} strokeWidth={2} />
      <Label x={cx} y={cy - 6} size={12} weight={700} color={C.white}>Router</Label>
      <Label x={cx} y={cy + 10} size={10} color={C.white}>× sensitivity</Label>
      <Label x={400} y={340} size={11} weight={600} color={C.inkMute}>
        Sensitivity floor · circuit breaker · A/B · shadow · ledger-anchored
      </Label>
    </Frame>
  );
}

function DiagramGenerativeWorkspace(): JSX.Element {
  const tiers = [
    { label: 'Public', color: '#e0e5f0' },
    { label: 'Internal', color: C.bgHi },
    { label: 'Confidential', color: C.primaryLight },
    { label: 'Regulatory', color: C.primaryDark },
  ];
  return (
    <Frame>
      <Label x={400} y={30} size={11} weight={700} color={C.inkMute}>
        Composer with four sensitivity tiers · every draft ledger-anchored
      </Label>
      {/* Composer window */}
      <Box x={100} y={80} w={600} h={220} fill={C.white} stroke={C.primary} strokeWidth={2} />
      <Box x={100} y={80} w={600} h={30} fill={C.primary} radius={6} />
      <Label x={400} y={98} size={12} weight={700} color={C.white}>GenWork</Label>

      {/* Sensitivity picker */}
      <Label x={120} y={135} anchor="start" size={11} weight={700} color={C.ink}>Sensitivity:</Label>
      {tiers.map((t, i) => {
        const x = 210 + i * 105;
        return (
          <g key={t.label}>
            <Box x={x} y={122} w={95} h={26} fill={t.color} stroke={C.primary} radius={13} />
            <Label x={x + 47} y={137} size={11} weight={600} color={i >= 2 ? C.white : C.ink}>{t.label}</Label>
          </g>
        );
      })}

      {/* Draft area */}
      <rect x={120} y={165} width={560} height={90} rx={4} fill={C.bgAlt} stroke={C.border} />
      <Label x={400} y={195} size={11} color={C.inkMute}>Long-form document / short-form Q&amp;A / slide-deck outline</Label>
      <Label x={400} y={220} size={10} color={C.inkDim}>Routing chosen per selected tier</Label>

      {/* Save badge */}
      <Box x={480} y={265} w={190} h={26} fill={C.primary} radius={13} />
      <Label x={575} y={280} size={11} weight={700} color={C.white}>Save → ledger entry</Label>
    </Frame>
  );
}

function DiagramWhatIf(): JSX.Element {
  const dots = [130, 200, 270, 340, 410, 480, 550, 620];
  return (
    <Frame>
      <Label x={400} y={30} size={11} weight={700} color={C.inkMute}>
        Replay past decisions under an alternate policy · reproducible from the ledger tip
      </Label>
      {/* Main ledger chain */}
      <line x1={70} y1={150} x2={720} y2={150} stroke={C.primary} strokeWidth={2} />
      {dots.map((x, i) => (
        <g key={i}>
          <circle cx={x} cy={150} r={9} fill={i === 4 ? C.primaryLight : C.primary} stroke={C.primaryDark} strokeWidth={1.5} />
          <Label x={x} y={130} size={9} color={C.inkMute}>{`e${i + 1}`}</Label>
        </g>
      ))}
      <Label x={70} y={170} anchor="start" size={11} weight={700} color={C.primary}>Genesis</Label>
      <Label x={720} y={170} anchor="end" size={11} weight={700} color={C.primary}>Tip (now)</Label>

      {/* Simulation branch */}
      <path d={`M 410 150 Q 470 230, 550 260 Q 620 280, 700 260`} fill="none" stroke={C.primaryLight} strokeWidth={2} strokeDasharray="5 4" />
      {[490, 570, 640].map((x, i) => (
        <circle key={i} cx={x} cy={i === 0 ? 240 : i === 1 ? 265 : 260} r={7} fill={C.primaryLight} />
      ))}
      <Box x={470} y={285} w={280} h={40} fill={C.bgHi} stroke={C.primary} />
      <Label x={610} y={306} size={11} weight={700} color={C.primary}>Simulation replay · alternate policy</Label>

      <Label x={410} y={110} size={10} color={C.inkMute}>Replay key ties to this ledger tip</Label>
    </Frame>
  );
}

function DiagramInsights(): JSX.Element {
  return (
    <Frame>
      <Label x={400} y={30} size={11} weight={700} color={C.inkMute}>Insights homepage · top-3 mission cards + today\'s brief</Label>
      {/* Three mission cards */}
      {[
        { color: C.green, label: 'Mission A', sub: '3 targets green', badge: 'On track' },
        { color: C.amber, label: 'Mission B', sub: '1 amber · action today', badge: 'Watch' },
        { color: C.red, label: 'Mission C', sub: '2 signatures overdue', badge: 'Escalate' },
      ].map((m, i) => (
        <g key={i}>
          <Box x={70 + i * 240} y={60} w={200} h={130} fill={C.white} stroke={C.primary} />
          <Box x={70 + i * 240} y={60} w={200} h={8} fill={m.color} radius={4} />
          <Label x={170 + i * 240} y={100} size={13} weight={700}>{m.label}</Label>
          <Label x={170 + i * 240} y={125} size={11} color={C.inkMute}>{m.sub}</Label>
          <Box x={110 + i * 240} y={148} w={120} h={26} fill={m.color} radius={13} />
          <Label x={170 + i * 240} y={163} size={11} weight={700} color={C.white}>{m.badge}</Label>
        </g>
      ))}
      {/* Today's brief */}
      <Box x={70} y={215} w={660} h={100} fill={C.bgAlt} stroke={C.primary} />
      <Label x={90} y={238} anchor="start" size={12} weight={700}>Today\'s brief</Label>
      <Label x={90} y={262} anchor="start" size={11} color={C.inkMute}>· 4 amber and 1 red band across your missions</Label>
      <Label x={90} y={280} anchor="start" size={11} color={C.inkMute}>· 3 decisions await your signature</Label>
      <Label x={90} y={298} anchor="start" size={11} color={C.inkMute}>· 1 anomaly signal (S2) — projected breach in 8 days</Label>
    </Frame>
  );
}

function DiagramDevelopers(): JSX.Element {
  const ides = ['Claude Code', 'Cursor', 'Continue.dev', 'OpenAI SDK'];
  return (
    <Frame>
      <Label x={400} y={30} size={11} weight={700} color={C.inkMute}>Developer IDEs route through the same Sovereign Proxy</Label>
      {ides.map((s, i) => (
        <g key={s}>
          <Box x={40 + i * 190} y={70} w={160} h={50} fill={C.bgAlt} stroke={C.primary} />
          <Label x={120 + i * 190} y={95} size={12} weight={700}>{s}</Label>
          <Arrow x1={120 + i * 190} y1={120} x2={400} y2={160} />
        </g>
      ))}
      {/* Personal token */}
      <Box x={280} y={155} w={240} h={40} fill={C.bgHi} stroke={C.primary} />
      <Label x={400} y={175} size={12} weight={700} color={C.primary}>Personal API Token · scope-typed · revocable</Label>
      <Arrow x1={400} y1={195} x2={400} y2={220} />
      {/* Proxy */}
      <Box x={280} y={220} w={240} h={50} fill={C.primary} stroke={C.primaryDark} />
      <Label x={400} y={247} size={13} weight={700} color={C.white}>Sovereign Proxy</Label>
      <Arrow x1={400} y1={270} x2={400} y2={295} />
      {/* Models */}
      <Box x={230} y={295} w={340} h={40} fill={C.white} stroke={C.primary} />
      <Label x={400} y={315} size={12} weight={700}>Model endpoints · same routing policy as every AI call</Label>
      <Label x={720} y={340} anchor="end" size={10} color={C.inkMute}>Every call: code_assistance_call ledger entry</Label>
    </Frame>
  );
}

function DiagramLedger(): JSX.Element {
  return (
    <Frame>
      <Label x={400} y={30} size={11} weight={700} color={C.inkMute}>Hash-chained per tenant · 25+ action classes · signed export bundles</Label>
      {/* Chain */}
      {['Genesis', 'e1', 'e2', 'e3', 'e4', '…', 'tip'].map((label, i) => {
        const x = 60 + i * 105;
        return (
          <g key={i}>
            <Box x={x} y={110} w={80} h={60} fill={i === 0 || i === 6 ? C.primary : C.white} stroke={C.primary} />
            <Label x={x + 40} y={130} size={11} weight={700} color={i === 0 || i === 6 ? C.white : C.ink}>{label}</Label>
            <Label x={x + 40} y={148} size={9} color={i === 0 || i === 6 ? C.white : C.inkMute}>hash</Label>
            {i < 6 && <Arrow x1={x + 80} y1={140} x2={x + 105} y2={140} />}
          </g>
        );
      })}
      {/* External anchor */}
      <Arrow x1={400} y1={175} x2={400} y2={220} />
      <Box x={250} y={220} w={300} h={50} fill={C.bgHi} stroke={C.primary} />
      <Label x={400} y={240} size={12} weight={700} color={C.primary}>External anchor · S3 Object Lock (WORM)</Label>
      <Label x={400} y={258} size={10} color={C.inkMute}>Tenant holds a copy the vendor cannot reach</Label>
      {/* Export bundle */}
      <Box x={250} y={290} w={300} h={40} fill={C.primary} stroke={C.primaryDark} />
      <Label x={400} y={310} size={12} weight={700} color={C.white}>Signed export bundle + included verifier → Regulator</Label>
    </Frame>
  );
}

function DiagramMlLayer(): JSX.Element {
  const stages = [
    { id: 'S5', label: 'Routing-policy learner', color: C.primary },
    { id: 'S4', label: 'On-tenant Sovereign LLM (opt-in GPU)', color: C.primary },
    { id: 'S3', label: 'Learned Mission Control ranker', color: C.primary },
    { id: 'S2', label: 'KPI anomaly detection', color: C.primary },
    { id: 'S1', label: 'On-tenant embeddings', color: C.primary },
    { id: 'S0', label: 'Foundations · Registry + Runner + Ledger classes', color: C.primaryDark },
  ];
  return (
    <Frame>
      {/* Tenant boundary */}
      <rect x={40} y={30} width={720} height={300} rx={12} fill={C.bgAlt} stroke={C.primary} strokeWidth={2} strokeDasharray="6 4" />
      <Label x={60} y={52} anchor="start" size={11} weight={700} color={C.primary}>YOUR TENANT BOUNDARY</Label>
      {stages.map((s, i) => {
        const y = 68 + i * 42;
        return (
          <g key={s.id}>
            <Box x={80} y={y} w={640} h={34} fill={s.color} radius={6} />
            <Label x={110} y={y + 17} anchor="start" size={12} weight={700} color={C.white}>{s.id}</Label>
            <Label x={400} y={y + 17} size={12} weight={600} color={C.white}>{s.label}</Label>
          </g>
        );
      })}
      <Label x={400} y={340} size={11} weight={600} color={C.inkMute}>
        S1–S5 opt-in per component · every promotion is a signed human decision
      </Label>
    </Frame>
  );
}

function DiagramEuAiAct(): JSX.Element {
  const rows = [
    ['Article 6', 'Mission class + sensitivity tiers'],
    ['Article 9', 'Signature Rules + step-up MFA'],
    ['Article 10', 'Endpoint contracts + redaction'],
    ['Article 12', 'Immutable Ledger + What-If replay'],
    ['Article 13', 'Per-decision detail + routing anchor'],
    ['Article 14', '"No policy change without signed decision"'],
    ['Article 15', 'Machine Learning Layer fairness gate + drift detector'],
  ];
  return (
    <Frame>
      <Label x={200} y={35} size={12} weight={700} color={C.primary}>EU AI Act</Label>
      <Label x={600} y={35} size={12} weight={700} color={C.primary}>DI Deck platform capability</Label>
      {rows.map((r, i) => {
        const y = 60 + i * 40;
        return (
          <g key={i}>
            <Box x={70} y={y} w={200} h={30} fill={C.bgAlt} stroke={C.primary} />
            <Label x={170} y={y + 15} size={11} weight={700}>{r[0]!}</Label>
            <Arrow x1={270} y1={y + 15} x2={340} y2={y + 15} />
            <Box x={340} y={y} w={390} h={30} fill={C.white} stroke={C.primary} />
            <Label x={535} y={y + 15} size={11} weight={600}>{r[1]!}</Label>
          </g>
        );
      })}
    </Frame>
  );
}

// ── Dispatcher ───────────────────────────────────────────────────────

const DIAGRAM_BY_SLUG: Record<string, () => JSX.Element> = {
  'what-is-dideck': DiagramWhatIsDiDeck,
  cockpit: DiagramCockpit,
  'sovereign-proxy': DiagramSovereignProxy,
  hierarchy: DiagramHierarchy,
  targets: DiagramTargets,
  decisions: DiagramDecisions,
  synapse: DiagramSynapse,
  'ai-gateway': DiagramAiGateway,
  'genwork': DiagramGenerativeWorkspace,
  'what-if': DiagramWhatIf,
  insights: DiagramInsights,
  developers: DiagramDevelopers,
  ledger: DiagramLedger,
  'ml-layer': DiagramMlLayer,
  'eu-ai-act': DiagramEuAiAct,
};
